/**
 * Typed mirror of the Flex Transactions read API (Checkpoint 1, select-only).
 *
 * Two endpoints share this exact request/response shape; only the field list and the blob that
 * backs dynamic keys differ:
 *  - POST /flex-transactions/query          — Enfusion side, dynamic keys resolve from `tranData`
 *                                             and keep their native JSON type.
 *  - POST /flex-external-transactions/query — Prometheus side, dynamic keys resolve from `aggInfo`
 *                                             and always come back as strings.
 *
 * `helios-session-id` is required on both. It is attached globally by the app's SessionInterceptor,
 * so no call site sets it.
 */

/**
 * The filter vocabulary — the `filterType` discriminator on a filter entry. Lowercase, and it has a
 * `set` member the sort vocabulary has no equivalent for.
 *
 * On a STATIC column this is also Jackson's polymorphic type id for the typed property on
 * `FlexTransactionsRequestFilters` / `FlexExternalTransactionsRequestFilters`, so a mismatch with
 * the declared slot is a 400 on deserialization rather than a wrong answer — see
 * `STATIC_SET_SLOT_FIELDS` in `grid/col-def-factory.ts`. On a DYNAMIC key it is the Postgres cast
 * target, so it must never be inferred from anything but the catalog type or the user's chosen type.
 */
export type QueryFilterType =
  | 'number'
  | 'date'
  | 'datetime'
  | 'time'
  | 'text'
  | 'set'

/**
 * The sort vocabulary — `SortField.type`. Uppercase, with a `BOOL` member the filter vocabulary has
 * no equivalent for, and no `set`. Case-insensitive server-side; an unknown token is a 400.
 *
 * Only consulted for dynamic keys (a static column sorts by its SQL type), but always sent: an
 * omitted type makes the backend fall back to a text comparison, so a numeric column would silently
 * sort 1000 before 9.
 */
export type QuerySortType =
  | 'NUM'
  | 'DATE'
  | 'DATETIME'
  | 'TIME'
  | 'BOOL'
  | 'TEXT'

/** One entry of the `sort` array, in user-chosen precedence order. */
export interface QuerySort {
  field: string
  direction: 'ASC' | 'DESC'
  /** Always populated — see {@link QuerySortType}. */
  type: QuerySortType
}

/**
 * One column's filter, as `@helios/server-side-util`'s `convertFilters` emits it. Deliberately
 * opaque here: this panel never constructs or inspects a filter body, it only decides which of the
 * two buckets the entry belongs in (PVAL-26129 §3).
 */
export type QueryFilter = unknown

/** A `field -> filter` map. */
export type FieldFilters = Record<string, QueryFilter>

/**
 * The two-bucket filter contract.
 *
 * Promoted (static) columns sit at the top level; JSONB-backed (dynamic) keys sit under
 * `dynamicFilters`. Getting it wrong fails asymmetrically, which is why §9 pins both directions:
 *  - a STATIC name inside `dynamicFilters` is a 400 (`dynamicFilters key(s) collide with static
 *    field(s)`);
 *  - a DYNAMIC key at the top level is **silently ignored** — `FAIL_ON_UNKNOWN_PROPERTIES` is
 *    disabled globally, so the filter does nothing, the rows come back unfiltered and there is no
 *    error to notice.
 *
 * `dynamicFilters` is omitted entirely when it would be empty, rather than sent as `{}`.
 */
export interface RequestFilter extends FieldFilters {
  dynamicFilters?: FieldFilters
}

/** The error code flex-positions returns for a chosen type Postgres could not apply (PVAL-26129 §6). */
export const INVALID_FIELD_TYPE = 'INVALID_FIELD_TYPE'

/**
 * The 400 body flex-positions sends for that case.
 *
 * flex-transactions does NOT send it yet — both of its query services let the Postgres cast error
 * through, so the UI currently gets a 500 and reports it with an error toast and an empty table. The
 * classifier and the Reset-to-Text path are built now so the behaviour lights up the moment the
 * backend ports `asFieldTypeError` (raised as a follow-up, not a blocker).
 */
export interface InvalidFieldTypeBody {
  error?: string
  fields?: string[]
  requestedType?: string
  value?: unknown
  message?: string
}

/** Request body. `group` is accepted by the schema but not implemented server-side. */
export interface DataRequest {
  /** Inclusive. */
  startRow: number
  /** Exclusive. */
  endRow: number
  /** Omit or null to get every static field and no dynamic ones. */
  fields?: string[] | null
  /** In precedence order, or null for the endpoint's own default order. */
  sort?: QuerySort[] | null
  /** The two-bucket filter contract, or null when nothing is applied. */
  filter?: RequestFilter | null
  /** Not supported yet — always null. */
  group?: null
}

/** One object per row, keyed by exactly the field names that were requested. */
export interface GridResponse {
  data: Array<Record<string, unknown>>
}

/**
 * There is deliberately NO hand-maintained list of promoted ("static") field names in this panel.
 *
 * The two lists that used to live here (`ENFUSION_STATIC_FIELDS` / `PROMETHEUS_STATIC_FIELDS`) were
 * removed by PVAL-26129: the bucket a filter or sort goes in is decided solely by the backend's
 * per-column `isDynamic` (see {@link RequestFilter} and `grid/filters/request-filter.builder.ts`).
 * A name list cannot answer the question correctly — an Enfusion `tran_data` column can arrive in
 * the DEFAULT group and still be dynamic — and a second copy of the backend's schema in the UI goes
 * stale silently, which for this contract means rows that come back unfiltered with no error.
 *
 * `STATIC_SET_SLOT_FIELDS` in `grid/col-def-factory.ts` is NOT a counter-example: it names the
 * fields whose backend filter *slot* is a `SetFilter` regardless of catalog type, which the catalog
 * genuinely does not publish yet. It picks a widget; it never picks a bucket.
 */

/** The identity field each endpoint sorts by and that the UI uses as the row id. */
export const ROW_ID_FIELD = {
  api: 'transactionId',
  prom: 'extTranId',
} as const

/**
 * The per-counterpart match columns (helios !499, PVAL-26557), which replaced the `matchedIds` /
 * `matchCounts` JSON blobs — those no longer exist and come back as silent nulls if requested.
 *
 * One pair per counterpart: `<cp>Matches` is the count (an int, `NUM`, filterable and sortable) and
 * `<cp>MatchesIds` the matched ids (a distinct, sorted `string[]`, `TEXT_LIST`, set-filterable by
 * overlap, never sortable). Each side carries only its own counterparts, and both are STATIC columns —
 * a filter on one goes at the top level of `filter`, never under `dynamicFilters` (a 400).
 *
 *  - Enfusion (`/flex-transactions`): custody, trading, clearwater.
 *  - Prometheus (`/flex-external-transactions`): enfusion, clearwater.
 *
 * The clearwater pair is always `0` / `[]` — Clearwater is never a flex counterpart yet — and
 * `clearwaterMatchesIds` is not advertised in the catalog (it still exists and can be requested).
 */
export const MATCH_COLUMN_PAIRS = {
  api: [
    { target: 'cust', count: 'custodyMatches', ids: 'custodyMatchesIds' },
    { target: 'trad', count: 'tradingMatches', ids: 'tradingMatchesIds' },
    { target: 'cw', count: 'clearwaterMatches', ids: 'clearwaterMatchesIds' },
  ],
  prom: [
    { target: 'api', count: 'enfusionMatches', ids: 'enfusionMatchesIds' },
    { target: 'cw', count: 'clearwaterMatches', ids: 'clearwaterMatchesIds' },
  ],
} as const

const matchFieldsOf = (
  pairs: ReadonlyArray<{ count: string; ids: string }>,
): string[] => pairs.flatMap(pair => [pair.count, pair.ids])

/** Fields the workbench always needs, whether or not the column catalog lists them. */
export const REQUIRED_FIELDS = {
  api: [
    'transactionId',
    'accountId',
    'instrumentType',
    'gLEntryId',
    // The Enfusion trade id, which is what `POST trades/get` reads by (PVAL-26492) — distinct from
    // `transactionId`, the workbench's own row id. Requested here rather than relied on from the
    // column catalog: a user who hides the Trade ID column would otherwise stop the Edit dialog
    // prefilling, with nothing on screen to connect the two.
    'tradeId',
    // Half of the flex primary key `POST transactions/matches` reads by (PVAL-26451), alongside
    // `transactionId`. Requested for the same reason as `tradeId`: a user who hides the Transaction
    // Type column would otherwise send a blank type, which skips the matches call and leaves the
    // Edit dialog's Custody/Trading comparison empty with nothing on screen to say why.
    'transactionType',
    // Every match pair, whether or not the catalog advertises it: MatchingService builds the match
    // index, the count columns and the cross-grid relationships from them. (They replace the removed
    // `matchedIds` / `matchCounts` blobs — helios !499.)
    ...matchFieldsOf(MATCH_COLUMN_PAIRS.api),
    'matchStatus',
    // Per-row permitted actions, which gate Match / Unmatch / Delete / Ignore in the context menu
    // (`utils/available-actions.ts`). Requested on both sources; a response that omits it leaves the
    // menu ungated rather than disabling everything.
    'availableActions',
  ],
  prom: [
    'extTranId',
    'accountId',
    'aggInfo',
    'extTranSource',
    ...matchFieldsOf(MATCH_COLUMN_PAIRS.prom),
    'matchStatus',
    'availableActions',
  ],
} as const
